import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Upload, 
  X, 
  Save, 
  Eye, 
  EyeOff,
  Calendar,
  Clock,
  Tag,
  Image as ImageIcon,
  FileText,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2
} from 'lucide-react';
import enhancedBlogService, { EnhancedBlog } from '../services/enhancedBlogService';

interface BlogFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  authorName: string;
  authorBio: string;
  readTime: string | number;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  status: 'draft' | 'published';
}

const CreateBlog: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Partial<BlogFormData>>({});
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    authorName: '',
    authorBio: '',
    readTime: '5',
    metaTitle: '',
    metaDescription: '',
    keywords: [],
    status: 'draft'
  });

  const categories = [
    'Implementation Guide',
    'Best Practices',
    'Case Study',
    'Technical Deep Dive',
    'Industry Insights',
    'Product Updates',
    'Tutorial',
    'News & Events'
  ];

  const handleInputChange = (field: keyof BlogFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }

    // Auto-generate meta title from title
    if (field === 'title') {
      setFormData(prev => ({ ...prev, metaTitle: value }));
    }

    // Auto-generate excerpt from content
    if (field === 'content') {
      const excerpt = value.substring(0, 160).trim();
      setFormData(prev => ({ ...prev, excerpt }));
    }

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Image size should be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const addKeyword = () => {
    if (newTag.trim() && !formData.keywords.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, keywords: [...prev.keywords, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    setFormData(prev => ({ ...prev, keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove) }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<BlogFormData> = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.excerpt.trim()) newErrors.excerpt = 'Excerpt is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.authorName.trim()) newErrors.authorName = 'Author name is required';
    if (!formData.authorBio.trim()) newErrors.authorBio = 'Author bio is required';
    const readTimeNum = parseInt(formData.readTime.toString());
    if (isNaN(readTimeNum) || readTimeNum < 1) newErrors.readTime = 'Read time must be at least 1 minute';
    if (!uploadedImage) newErrors.content = 'Featured image is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('image', uploadedImage!);
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, value.toString());
        }
      });

      await enhancedBlogService.createBlog(submitData as any);
      
      setSuccessMessage('Blog created successfully!');
      setTimeout(() => {
        navigate('/blog');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating blog:', error);
      setErrors({ content: 'Failed to create blog. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setFormData(prev => ({ ...prev, status: 'draft' }));
    await handleSubmit(new Event('submit') as any);
  };

  const handlePublish = async () => {
    setFormData(prev => ({ ...prev, status: 'published' }));
    await handleSubmit(new Event('submit') as any);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Blog</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  showPreview 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                <span>{showPreview ? 'Hide Preview' : 'Show Preview'}</span>
              </button>
              
              <button
                onClick={handleSaveDraft}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <Save size={16} />
                <span>Save Draft</span>
              </button>
              
              <button
                onClick={handlePublish}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Save size={16} />
                <span>{isLoading ? 'Publishing...' : 'Publish'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-custom py-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className={`${showPreview ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Blog Post</h1>

                {/* Success Message */}
                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 mb-6"
                  >
                    <CheckCircle size={20} />
                    <span>{successMessage}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <FileText size={20} className="text-blue-600" />
                      <span>Basic Information</span>
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.title ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter blog post title..."
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle size={14} />
                          <span>{errors.title}</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Slug *
                      </label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.slug ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="blog-post-url-slug"
                      />
                      {errors.slug && (
                        <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle size={14} />
                          <span>{errors.slug}</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Excerpt *
                      </label>
                      <textarea
                        value={formData.excerpt}
                        onChange={(e) => handleInputChange('excerpt', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.excerpt ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Brief description of the blog post..."
                      />
                      {errors.excerpt && (
                        <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle size={14} />
                          <span>{errors.excerpt}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <FileText size={20} className="text-green-600" />
                      <span>Content</span>
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content *
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => handleInputChange('content', e.target.value)}
                        rows={12}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm ${
                          errors.content ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Write your blog content here... (HTML supported)"
                      />
                      {errors.content && (
                        <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle size={14} />
                          <span>{errors.content}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <ImageIcon size={20} className="text-purple-600" />
                      <span>Featured Image</span>
                    </h2>
                    
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      {imagePreview ? (
                        <div className="space-y-4">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-w-full h-64 object-cover rounded-lg mx-auto"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors mx-auto"
                          >
                            <Trash2 size={16} />
                            <span>Remove Image</span>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Upload className="mx-auto h-12 w-12 text-gray-400" />
                          <div>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="text-blue-600 hover:text-blue-500 font-medium"
                            >
                              Click to upload
                            </button>
                            <span className="text-gray-500"> or drag and drop</span>
                          </div>
                          <p className="text-sm text-gray-500">
                            PNG, JPG, GIF up to 5MB
                          </p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <Tag size={20} className="text-orange-600" />
                        <span>Categories & Tags</span>
                      </h2>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category *
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.category ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select a category</option>
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                        {errors.category && (
                          <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                            <AlertCircle size={14} />
                            <span>{errors.category}</span>
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags
                        </label>
                        <div className="flex space-x-2 mb-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Add a tag..."
                          />
                          <button
                            type="button"
                            onClick={addTag}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.map(tag => (
                            <span
                              key={tag}
                              className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                            >
                              <span>{tag}</span>
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <Clock size={20} className="text-indigo-600" />
                        <span>Reading & SEO</span>
                      </h2>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Read Time (minutes) *
                        </label>
                        <input
                          type="number"
                          value={formData.readTime}
                          onChange={(e) => handleInputChange('readTime', parseInt(e.target.value))}
                          min="1"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.readTime ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors.readTime && (
                          <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                            <AlertCircle size={14} />
                            <span>{errors.readTime}</span>
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Title
                        </label>
                        <input
                          type="text"
                          value={formData.metaTitle}
                          onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="SEO title for search engines..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Description
                        </label>
                        <textarea
                          value={formData.metaDescription}
                          onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Brief description for search engines..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Keywords
                        </label>
                        <div className="flex space-x-2 mb-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Add a keyword..."
                          />
                          <button
                            type="button"
                            onClick={addKeyword}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.keywords.map(keyword => (
                            <span
                              key={keyword}
                              className="flex items-center space-x-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                            >
                              <span>{keyword}</span>
                              <button
                                type="button"
                                onClick={() => removeKeyword(keyword)}
                                className="text-indigo-600 hover:text-indigo-800"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Author Information */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <Calendar size={20} className="text-teal-600" />
                      <span>Author Information</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Author Name *
                        </label>
                        <input
                          type="text"
                          value={formData.authorName}
                          onChange={(e) => handleInputChange('authorName', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.authorName ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter author name..."
                        />
                        {errors.authorName && (
                          <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                            <AlertCircle size={14} />
                            <span>{errors.authorName}</span>
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Author Bio *
                        </label>
                        <textarea
                          value={formData.authorBio}
                          onChange={(e) => handleInputChange('authorBio', e.target.value)}
                          rows={3}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.authorBio ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Brief author biography..."
                        />
                        {errors.authorBio && (
                          <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                            <AlertCircle size={14} />
                            <span>{errors.authorBio}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </motion.div>
            </div>

            {/* Preview Section */}
            {showPreview && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-1"
              >
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
                  
                  {formData.title && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">Title</h3>
                        <p className="text-gray-700">{formData.title}</p>
                      </div>
                      
                      {formData.excerpt && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Excerpt</h3>
                          <p className="text-gray-700 text-sm">{formData.excerpt}</p>
                        </div>
                      )}
                      
                      {formData.category && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Category</h3>
                          <span className="inline-block bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                            {formData.category}
                          </span>
                        </div>
                      )}
                      
                      {formData.tags.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Tags</h3>
                          <div className="flex flex-wrap gap-1">
                            {formData.tags.map(tag => (
                              <span key={tag} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {formData.authorName && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Author</h3>
                          <p className="text-gray-700">{formData.authorName}</p>
                        </div>
                      )}
                      
                      {formData.readTime && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Read Time</h3>
                          <p className="text-gray-700">{formData.readTime} min read</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!formData.title && (
                    <p className="text-gray-500 text-center py-8">
                      Start filling out the form to see a preview here
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBlog;
